import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Box, Typography } from "@mui/material";
import { PRODUCTS } from "../data/products";
import { useSEO } from "../lib/seo";
import { Reveal } from "../site/Reveal";
import { CertMarquee } from "../site/CertMarquee";
import { CTASection } from "../site/CTASection";

const MotionTypography = motion(Typography);

export default function ProductsPages() {
  useSEO({
    title:
      "Products | Servo Stabilizers, Isolation Transformers & CVTs | ITL Technical Services",
    description:
      "Explore ITL Technical Services' range of power conditioning equipment: servo controlled voltage stabilizers, super isolation transformers, constant voltage transformers and electrical equipment supply.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: PRODUCTS.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: p.name,
      })),
    },
  });

  return (
    <>
      {/* HEADER */}
      <Box
        data-testid="products-header"
        className="grain grain-dark"
        sx={{
          position: "relative",
          color: "#fff",
          overflow: "hidden",
          pt: { xs: 16, md: 22 },
          pb: { xs: 8, md: 12 },

          backgroundImage: `
      linear-gradient(
        rgba(10,10,10,0.75),
        rgba(10,10,10,0.82)
      ),
      url("/ProductBanner.jpg")
    `,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Box
          sx={{
            maxWidth: "1280px", // max-w-7xl
            mx: "auto",
            px: { xs: 2, sm: 3, lg: 4 }, // px-4 sm:px-6 lg:px-8
          }}
        >
          <Typography
            className="mono-label"
            sx={{
              fontFamily: "var(--font-mono, monospace)",
              fontSize: "0.75rem",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: "var(--color-safety, #ff5a1f)",
              mb: 4, // mb-8
              textAlign: "left",
            }}
          >
            [ Products ]
          </Typography>

          <MotionTypography
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            component="h1"
            sx={{
              fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
              fontWeight: 900, // font-black
              fontSize: { xs: "3rem", md: "4.5rem", lg: "6rem" }, // text-5xl md:text-7xl lg:text-8xl
              letterSpacing: "-0.05em", // tracking-tighter
              lineHeight: 0.85, // leading-[0.85]
              maxWidth: "896px", // max-w-4xl
              textAlign: "left",
            }}
          >
            Power conditioning, engineered.
          </MotionTypography>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.6)", // text-white/60
              mt: 4, // mt-8
              maxWidth: "576px", // max-w-xl
              fontSize: "1.125rem", // text-lg
              textAlign: "left",
            }}
          >
            Four categories. One standard of precision — from single stabilizers
            to complete electrical supply.
          </Typography>
        </Box>
      </Box>

      <CertMarquee />

      {/* PRODUCT LIST */}
      <Box
        sx={{
          maxWidth: "1280px", // max-w-7xl
          mx: "auto",
          px: { xs: 2, sm: 3, lg: 4 }, // px-4 sm:px-6 lg:px-8
          py: { xs: 8, md: 12 }, // py-16 md:py-24
          display: "flex",
          flexDirection: "column",
          gap: 2, // space-y-4
        }}
      >
        {PRODUCTS.map((p, i) => (
          <Reveal key={p.slug} delay={i * 0.05}>
            <Box
              component={Link}
              to={`/products/${p.slug}`}
              data-testid={`products-list-${p.slug}`}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(12, 1fr)" },
                gap: { xs: 3, md: 4 }, // gap-6 md:gap-8
                alignItems: "center",
                border: "1px solid rgba(17,17,17,0.15)", // border-ink/15
                p: { xs: 2, md: 3 }, // p-4 md:p-6
                textDecoration: "none",
                color: "inherit",
                transition: "border-color 0.2s, background-color 0.2s",
                "&:hover": {
                  borderColor: "var(--color-safety, #ff5a1f)",
                  bgcolor: "#fff",
                  "& .prod-image": {
                    filter: "grayscale(0)",
                  },
                  "& .prod-title": {
                    color: "var(--color-safety, #ff5a1f)",
                  },
                  "& .prod-arrow": {
                    color: "var(--color-safety, #ff5a1f)",
                    transform: "rotate(45deg)",
                  },
                },
              }}
            >
              {/* Image */}
              <Box
                sx={{
                  gridColumn: { md: "span 4" },
                  aspectRatio: "4 / 3",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={p.image}
                  alt={p.alt}
                  loading="lazy"
                  className="prod-image"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    filter: "grayscale(1)",
                    transition: "filter 0.7s, transform 0.7s",
                  }}
                />
              </Box>

              {/* Text */}
              <Box sx={{ gridColumn: { md: "span 7" } }}>
                <Typography
                  sx={{
                    textAlign: "left",
                    fontFamily: "var(--font-mono, monospace)",
                    fontSize: "0.75rem",
                    color: "var(--color-safety, #ff5a1f)",
                  }}
                >
                  [{p.index}]
                </Typography>

                <Typography
                  component="h2"
                  className="prod-title"
                  sx={{
                    fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                    fontWeight: 900, // font-black
                    fontSize: { xs: "1.875rem", md: "2.25rem" }, // text-3xl md:text-4xl
                    letterSpacing: "-0.05em", // tracking-tighter
                    color: "var(--color-ink, #111)",
                    mt: 1, // mt-2
                    transition: "color 0.2s",
                    textAlign: "left",
                  }}
                >
                  {p.name}
                </Typography>

                <Typography
                  sx={{
                    fontFamily: "var(--font-mono, monospace)",
                    fontSize: "0.875rem", // text-sm
                    color: "var(--color-muted-foreground, #6b7280)",
                    mt: 1.5, // mt-3
                    textAlign: "left",
                  }}
                >
                  {p.tagline}
                </Typography>

                <Typography
                  sx={{
                    color: "var(--color-muted-foreground, #6b7280)",
                    mt: 2, // mt-4
                    maxWidth: "672px", // max-w-2xl
                    display: "-webkit-box",
                    WebkitLineClamp: 2, // line-clamp-2
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textAlign: "left",
                    fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                  }}
                >
                  {p.description}
                </Typography>
              </Box>

              {/* Arrow */}
              <Box
                sx={{
                  gridColumn: { md: "span 1" },
                  display: "flex",
                  justifyContent: { md: "flex-end" },
                }}
              >
                <ArrowUpRight
                  className="prod-arrow"
                  style={{
                    width: 32,
                    height: 32,
                    color: "rgba(17,17,17,0.4)", // text-ink/40
                    transition: "color 0.2s, transform 0.2s",
                  }}
                />
              </Box>
            </Box>
          </Reveal>
        ))}
      </Box>

      <CTASection />
    </>
  );
}
